import { environmentConfig } from '@/config/enviromentConfig'
import { isMockMode } from '@/services/telkomPremiumService'

// The page's beacon to service.telkom-integration (POST /premium/events) for the Marketing Dashboard:
// what this visitor saw of Telkom's header enrichment and /sub, and real-user timings. Telkom's HE
// and /sub happen in the browser, so nothing else can measure them. Never in mock mode, so review
// links and local dev do not read as traffic. Best effort: a lost beacon costs a data point only.
let getContext = () => null
let queue = []
let timer = null
let perfSent = false
const paint = { fcp: null, lcp: null }

const enabled = () => !isMockMode() && Boolean(environmentConfig.integrationService)

// The labels the service needs for every event of this page load, read when a batch is sent.
export function configureTelemetry(contextFn) {
  getContext = contextFn
}

export function track(type, data = {}) {
  if (!enabled()) return
  queue.push({ type, ...data })
  if (!timer) timer = setTimeout(flush, 250)
}

export function flush() {
  clearTimeout(timer)
  timer = null
  if (!queue.length) return
  const context = getContext()
  if (!context) return
  const body = JSON.stringify({ v: 1, ...context, events: queue.splice(0, 12) })
  const url = `${environmentConfig.integrationService}/premium/events`
  try {
    // text/plain keeps it a simple CORS request: no preflight.
    const sent = navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }))
    if (!sent) fetch(url, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {})
  } catch {
    // nothing to do: the next event tries again
  }
  if (queue.length) timer = setTimeout(flush, 0)
}

// Milliseconds since navigation start (performance.now() shares that origin).
export const sinceNavigation = () => Math.round(performance.now())

// Paint timings as the browser reports them; LCP keeps moving until the first input or hide.
export function observePaints() {
  if (!enabled() || typeof PerformanceObserver === 'undefined') return
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) if (entry.name === 'first-contentful-paint') paint.fcp = Math.round(entry.startTime)
    }).observe({ type: 'paint', buffered: true })
    new PerformanceObserver((list) => {
      const last = list.getEntries().at(-1)
      if (last) paint.lcp = Math.round(last.startTime)
    }).observe({ type: 'largest-contentful-paint', buffered: true })
  } catch {
    // an older browser without these entry types: the navigation timings still go
  }
  // Sent once: when the visitor taps (LCP is final by then), leaves, or after ten seconds.
  const send = () => sendPerf()
  addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && send())
  addEventListener('pagehide', send)
  setTimeout(send, 10000)
}

export function sendPerf() {
  if (perfSent || !enabled()) return
  perfSent = true
  const nav = performance.getEntriesByType?.('navigation')?.[0]
  track('perf', {
    ttfb: nav ? Math.round(nav.responseStart) : null,
    domInteractive: nav ? Math.round(nav.domInteractive) : null,
    load: nav && nav.loadEventEnd > 0 ? Math.round(nav.loadEventEnd) : null,
    fcp: paint.fcp,
    lcp: paint.lcp
  })
  flush()
}
